import { editBanner } from '@/entities/banners/model/bannerSlice';
import { useNotification } from '@/shared/lib/hooks/useNotification';
import { useAppDispatch } from '@/shared/model';
import { Tooltip } from '@/shared/ui/Tooltip/Tooltip';
import classNames from 'classnames';
import React, { MouseEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { ReactComponent as EyeClose } from '../../../assets/svg/CloseEye.svg';
import { ReactComponent as EyeOpen } from '../../../assets/svg/OpenEye.svg';
import { periodText, SECTION_MAP, STATUS_MAP } from '../constants';

// --- Типы пропсов ---
interface SingleBannerProps {
  id: number;
  index: number;
  itemsCount: number;
  section: keyof typeof SECTION_MAP | string;
  title: string;
  status: keyof typeof STATUS_MAP | string;
  format: string;
  period: string;
  isPromotional: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  dateStart?: string;
  dateEnd?: string;
}

// --- Основной компонент ---
export const SingleBanner: React.FC<SingleBannerProps> = ({
  id,
  section,
  title,
  status,
  isPromotional,
  isFirst,
  isLast,
  dateStart,
  dateEnd,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPeriodVisible, setIsPeriodVisible] = useState(true);
  const dispatch = useAppDispatch();
  const notify = useNotification();

  // Обработчик скрытия/показа периода и смены статуса
  const togglePeriodVisibility = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsPeriodVisible((prev) => !prev);
    try {
      if (status === 'published' || status === 'planned') {
        await dispatch(editBanner({ id, data: { status: 'hidden' } }));
        notify({ value: 'Баннер скрыт', variant: 'success' });
      } else if (status === 'hidden') {
        const now = new Date();
        now.setMinutes(now.getMinutes() - 1);
        await dispatch(
          editBanner({
            id,
            data: {
              status: 'published',
              dateStart: now.toISOString(),
              dateEnd: null,
            },
          })
        );
        notify({ value: 'Баннер опубликован', variant: 'success' });
      }
    } catch (e: any) {
      notify({ value: e?.message || 'Ошибка', variant: 'error' });
    }
  };

  const statusText = STATUS_MAP[status as keyof typeof STATUS_MAP] || status;
  const formatText = isPromotional ? 'Реклама' : 'Объявление';

  return (
    <div className='mx-auto w-[1280px]'>
      <Link
        to={`/banners/${id}`}
        className={classNames(
          'h-18 relative flex items-center justify-start gap-8 self-stretch rounded-[16px] border-[#F3F3F3] bg-[#FFF] px-6 py-4 text-lg font-normal not-italic leading-6 text-[color:var(--Simple-Night,#000)] transition-all duration-75 hover:cursor-pointer hover:bg-[#F3F3F3]',
          isFirst
            ? 'rounded-t-[16px] border-l border-r border-t'
            : 'rounded-[0px] border-l border-r border-t',
          isLast ? 'rounded-b-[16px] border-b' : 'rounded-[0px]'
        )}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        <div className='col-span-1 w-[196px]'>
          {SECTION_MAP[section as keyof typeof SECTION_MAP] || section}
        </div>
        <div className='col-span-1 w-[496px] text-[#337CEC]'>
          {title || <span className='text-gray-400 mb'>-</span>}
        </div>
        <div className='col-span-1 w-[196px]'>{statusText}</div>
        <div className='col-span-1 w-[196px]'>{formatText}</div>
        <div className='relative col-span-1 w-[260px] pr-12'>
          {status === 'hidden' ? (
            <span className='text-gray-400 mb'>-</span>
          ) : isPeriodVisible && periodText(dateStart, dateEnd) ? (
            periodText(dateStart, dateEnd)
          ) : (
            <span className='text-gray-400 mb'>-</span>
          )}
          {isVisible && (
            <button
              data-testid={`button-toggle-${id}`}
              onClick={togglePeriodVisibility}
              className='absolute right-[0.75rem] top-1/2 -translate-y-1/2'
            >
              {status === 'hidden' ? (
                <Tooltip text='Вернуть'>
                  <EyeClose className='text-blue500 hover:text-blue' />
                </Tooltip>
              ) : (
                <Tooltip text={isPeriodVisible ? 'Скрыть' : 'Показать'}>
                  {isPeriodVisible ? (
                    <EyeOpen className='text-blue500 hover:text-blue' />
                  ) : (
                    <EyeClose className='text-blue500 hover:text-blue' />
                  )}
                </Tooltip>
              )}
            </button>
          )}
        </div>
      </Link>
    </div>
  );
};
